// Platform-admin DO read-only inspection — audits to Loki as admin_debug_read; 403 non-admin; 120/hour.
import * as fs from 'node:fs';
import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { confirm, parsePositiveIntFlag } from '../lib/utils.js';
import { runDebugObservability } from './observability.js';
import { parseReportEditArgs } from '../lib/report-edit-args.js';
import { findGitRoot } from '../lib/workspace.js';
import { HARNESS_SKILL_DIRS } from '../lib/skill-version.js';
import { writeFileMap } from '../lib/file-map.js';
import { extractApiMessage } from '../lib/errors.js';
import {
  dataExplorerDebugDoType,
  dataExplorerDebugCountsDoType,
  BRANCHING_ORPHAN_ENTITY_TYPES,
  dataExplorerDebugAnalyticsTable,
  ADMIN_SKILL_NAME_REGEX,
  voiceCallMinuteOps,
  MAX_VOICE_CALL_MINUTE_OPS,
  adminCreateCommunityBody,
  communityIdParam,
  COMMUNITIES_PER_ORG_MAX,
  COMMUNITY_REGISTRY_CLOSED_REASON,
  adminSetGuideHubBody,
  GUIDE_HUB_INELIGIBLE_REASON,
} from '@wayai/core/contracts';
import type {
  DataExplorerDebugDoType,
  DataExplorerDebugAnalyticsTable,
  CreateNoticeBody,
  NoticeSeverity,
  AdminHarnessMassDestroyInput,
  AdminRepairTeamOrphansInput,
  AdminRepairBranchingOrphansInput,
  AdminRepairBranchingOrphansResponse,
  AdminRepairKnowledgeBlobsInput,
  BranchingOrphanEntityType,
  AdminRepairLegacyOrgGrantsInput,
  UpdatePlatformConfigInput,
  AdminPeopleModerationResponse,
  AdminCreateCommunityResponse,
  AdminDestroyCommunityResponse,
  AdminGuideHub,
  AdminSetGuideHubResponse,
} from '@wayai/core/contracts';

type DoType = DataExplorerDebugDoType;
type AnalyticsTable = DataExplorerDebugAnalyticsTable;
// Single source of truth: the contract enums the backend allowlist also derives
// from. The CLI bundles @wayai/core at build (tsup `noExternal`), so importing
// the runtime enum is free — no hand-copied literal, no drift to guard.
const VALID_TYPES = dataExplorerDebugDoType.options;
const VALID_ANALYTICS_TABLES = dataExplorerDebugAnalyticsTable.options;

export async function adminCommand(args: string[]): Promise<void> {
  const [group, ...afterGroup] = args;

  if (!group) {
    printHelp();
    process.exit(1);
  }

  if (group === 'archive') {
    // `archive` has no sub — all remaining args are positional/flags.
    const { positional, flagArgs } = splitArgs(afterGroup);
    await runArchiveRead(positional, flagArgs);
    return;
  }

  if (group === 'observability') {
    const { positional, flagArgs } = splitArgs(afterGroup);
    await runObservabilityRead(positional, flagArgs);
    return;
  }

  const [sub, ...rest] = afterGroup;
  const { positional, flagArgs } = splitArgs(rest);

  if (group === 'do') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'tables': await runDoTables(positional); return;
      case 'read': await runDoRead(positional, flagArgs); return;
      case 'counts': await runDoCounts(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'analytics') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'tables': await runAnalyticsTables(); return;
      case 'read': await runAnalyticsRead(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'report') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'list': await runReportList(flagArgs); return;
      case 'show': await runReportShow(positional); return;
      case 'group': await runReportGroup(positional); return;
      case 'ungroup': await runReportUngroup(positional); return;
      case 'escalate': await runReportEscalate(positional, flagArgs); return;
      case 'shipped': await runReportShipped(positional, flagArgs); return;
      case 'addressed': await runReportAddressed(positional); return;
      case 'dismiss': await runReportDismiss(positional, flagArgs); return;
      case 'hold': await runReportHold(positional, flagArgs); return;
      case 'update': await runReportUpdate(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'people') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'list': await runPeopleList(flagArgs); return;
      case 'show': await runPeopleShow(positional); return;
      case 'dismiss': await runPeopleDismiss(positional, flagArgs); return;
      case 'remove-message': await runPeopleRemoveMessage(positional, flagArgs); return;
      case 'user': await runPeopleUser(positional); return;
      case 'suspend': await runPeopleSuspension(true, positional, flagArgs); return;
      case 'unsuspend': await runPeopleSuspension(false, positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'notice') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'create': await runNoticeCreate(flagArgs); return;
      case 'list': await runNoticeList(flagArgs); return;
      case 'resolve': await runNoticeResolve(positional); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'skill') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'install': await runSkillInstall(positional); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'hubs') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'repair-team-orphans': await runRepairTeamOrphans(flagArgs); return;
      case 'repair-branching-orphans': await runRepairBranchingOrphans(flagArgs); return;
      case 'repair-knowledge-blobs': await runRepairKnowledgeBlobs(flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'orgs') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'repair-legacy-grants': await runRepairLegacyOrgGrants(flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'harness') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'mass-destroy': await runHarnessMassDestroy(flagArgs); return;
      case 'disable': await runHarnessToggle(false, flagArgs); return;
      case 'enable': await runHarnessToggle(true, flagArgs); return;
      case 'status': await runPlatformGateStatus(HARNESS_GATE, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'threads') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'enable': await runThreadsToggle(true, flagArgs); return;
      case 'disable': await runThreadsToggle(false, flagArgs); return;
      case 'status': await runPlatformGateStatus(THREADS_GATE, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'communities') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'enable': await runCommunitiesToggle(true, flagArgs); return;
      case 'disable': await runCommunitiesToggle(false, flagArgs); return;
      case 'status': await runPlatformGateStatus(COMMUNITIES_GATE, flagArgs); return;
      case 'create': await runCommunitiesCreate(flagArgs); return;
      case 'destroy': await runCommunitiesDestroy(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'guide') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'set-hub': await runGuideSetHub(positional, flagArgs); return;
      case 'clear': await runGuideClear(positional, flagArgs); return;
      case 'status': await runGuideStatus(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'voice-calls') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'enable': await runVoiceCallsToggle(true, flagArgs); return;
      case 'disable': await runVoiceCallsToggle(false, flagArgs); return;
      case 'status': await runVoiceCallsStatus(flagArgs); return;
      case 'price': await runVoiceCallsPrice(flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  printHelp();
  process.exit(1);
}

function splitArgs(args: string[]): { positional: string[]; flagArgs: string[] } {
  const positional: string[] = [];
  const flagArgs: string[] = [];
  for (const a of args) {
    if (a.startsWith('--') || flagArgs.length > 0) {
      flagArgs.push(a);
    } else {
      positional.push(a);
    }
  }
  return { positional, flagArgs };
}

// ---------------------------------------------------------------------------
// Subcommands — DO
// ---------------------------------------------------------------------------

async function runDoTables(positional: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin do tables <type> <id>');
    process.exit(1);
  }
  const type = validateType(positional[0]);
  const id = positional[1];

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugListTables(type, id);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const { tables } = response.data;
  if (tables.length === 0) {
    console.log('(no allowlisted tables found on this DO)');
    return;
  }
  for (const t of tables) console.log(t);
}

async function runDoRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 3) {
    console.error('wayai admin do read <type> <id> <table> [--limit N] [--id X] [--json]');
    process.exit(1);
  }
  const type = validateType(positional[0]);
  const id = positional[1];
  const table = positional[2];

  const { limit, filterId, jsonOutput } = parseFlags(flagArgs, ['--limit', '--id', '--json']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadTable(type, id, table, { limit, id: filterId });
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  printDebugRowsResult(response.data, jsonOutput);
}

async function runDoCounts(positional: string[], flagArgs: string[]): Promise<void> {
  const type = dataExplorerDebugCountsDoType.safeParse(positional[0]);
  if (positional.length < 2 || !type.success) {
    console.error(`wayai admin do counts <${dataExplorerDebugCountsDoType.options.join('|')}> <id> [--json]`);
    process.exit(1);
  }
  const { jsonOutput } = parseFlags(flagArgs, ['--json']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugCounts(type.data, positional[1]);
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  if (jsonOutput) {
    console.log(JSON.stringify(response.data, null, 2));
    return;
  }
  for (const line of flattenCounts(response.data)) console.log(line);
}

/** `a.b.c: n` lines, one per number, for the counts' human output. */
function flattenCounts(value: unknown, prefix = ''): string[] {
  if (value === null || typeof value !== 'object') return [`${prefix}: ${value === null ? '—' : String(value)}`];
  return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) => flattenCounts(child, prefix ? `${prefix}.${key}` : key));
}

// ---------------------------------------------------------------------------
// Subcommands — Analytics (ClickHouse)
// ---------------------------------------------------------------------------

async function runAnalyticsTables(): Promise<void> {
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugListAnalyticsTables();
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  const { tables } = response.data;
  if (tables.length === 0) {
    console.log('(no analytics tables available)');
    return;
  }
  for (const t of tables) console.log(t);
}

async function runAnalyticsRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 1) {
    console.error('wayai admin analytics read <table> [--conversation-id X] [--hub-id Y] [--message-id Z] [--limit N] [--json]');
    process.exit(1);
  }
  const table = validateAnalyticsTable(positional[0]);

  const flags = parseFlags(flagArgs, ['--limit', '--json', '--conversation-id', '--hub-id', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadAnalyticsTable(table, {
      conversationId: flags.conversationId,
      hubId: flags.hubId,
      messageId: flags.messageId,
      limit: flags.limit,
    });
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  printDebugRowsResult(response.data, flags.jsonOutput);
}

// ---------------------------------------------------------------------------
// Subcommands — Archive (R2)
// ---------------------------------------------------------------------------

async function runArchiveRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin archive <hub_id> <conversation_id> [--message-id X] [--json]');
    process.exit(1);
  }
  const hubId = positional[0];
  const conversationId = positional[1];

  const flags = parseFlags(flagArgs, ['--json', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadArchive(hubId, conversationId, {
      messageId: flags.messageId,
    });
  } catch (err) {
    // Archive-specific 404 copy: an admin reading an archive expects "purged or never written"
    // rather than a generic "not found." Only this subcommand has the context to say that.
    if (err instanceof ApiError && err.status === 404) {
      console.error('Archive not found. The conversation may not be ended yet, or the R2 blob may have been purged.');
      process.exit(1);
    }
    exitOnApiError(err);
    throw err;
  }

  const data = response.data;

  if (flags.jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  // Default pretty-print: archive metadata + a one-line message digest.
  // Admins who need full content pass --json.
  const archive = data.archive;
  console.log(`hub_id:         ${data.hub_id}`);
  console.log(`conversation_id:${data.conversation_id}`);
  if (data.message_filtered) console.log('(messages filtered by --message-id)');

  const messages = Array.isArray(archive.messages) ? (archive.messages as Array<Record<string, unknown>>) : [];
  const files = Array.isArray(archive.files) ? archive.files.length : 0;
  console.log(`messages:       ${messages.length}`);
  console.log(`files:          ${files}`);

  if (messages.length > 0) {
    console.log('');
    console.log('messages (id | sender | created_at | text):');
    for (const m of messages) {
      const id = String(m.message_id ?? '');
      const sender = String(m.sender_type ?? '');
      const ts = String(m.created_at ?? '');
      const text = truncate(String(m.message_text ?? ''), 80);
      console.log(`  ${id} | ${sender} | ${ts} | ${text}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Observability (R2)
// ---------------------------------------------------------------------------

async function runObservabilityRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin observability <hub_id> <conversation_id> [--message-id X] [--json]');
    process.exit(1);
  }
  const hubId = positional[0];
  const conversationId = positional[1];

  const flags = parseFlags(flagArgs, ['--json', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    await runDebugObservability(client, hubId, conversationId, {
      messageId: flags.messageId,
      jsonOutput: flags.jsonOutput,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error('Observability data not found. Either the conversation has no LLM turns yet, or R2 entries were purged.');
      process.exit(1);
    }
    exitOnApiError(err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function validateType(raw: string | undefined): DoType {
  if (!raw || !VALID_TYPES.includes(raw as DoType)) {
    console.error(`type must be one of: ${VALID_TYPES.join(', ')}`);
    process.exit(1);
  }
  return raw as DoType;
}

function validateAnalyticsTable(raw: string | undefined): AnalyticsTable {
  if (!raw || !VALID_ANALYTICS_TABLES.includes(raw as AnalyticsTable)) {
    console.error(`table must be one of: ${VALID_ANALYTICS_TABLES.join(', ')}`);
    process.exit(1);
  }
  return raw as AnalyticsTable;
}

interface ParsedFlags {
  limit?: number;
  filterId?: string;
  conversationId?: string;
  hubId?: string;
  messageId?: string;
  jsonOutput: boolean;
}

function parseFlags(flagArgs: string[], allowed: readonly string[]): ParsedFlags {
  const out: ParsedFlags = { jsonOutput: false };
  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    if (!allowed.includes(arg)) {
      console.error(`Unknown flag: ${arg}`);
      process.exit(1);
    }
    if (arg === '--json') {
      out.jsonOutput = true;
      continue;
    }
    const v = flagArgs[++i];
    if (!v) { console.error(`${arg} requires a value`); process.exit(1); }
    if (arg === '--limit') {
      const n = Number.parseInt(v, 10);
      if (!Number.isFinite(n)) { console.error('--limit must be an integer'); process.exit(1); }
      if (n < 1) { console.error('--limit must be >= 1'); process.exit(1); }
      out.limit = n;
    } else if (arg === '--id') {
      out.filterId = v;
    } else if (arg === '--conversation-id') {
      out.conversationId = v;
    } else if (arg === '--hub-id') {
      out.hubId = v;
    } else if (arg === '--message-id') {
      out.messageId = v;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Subcommands — operator skill install (private, platform-admin gated)
// ---------------------------------------------------------------------------

async function runSkillInstall(positional: string[]): Promise<void> {
  const name = positional[0] ?? 'wayai-admin';
  if (!ADMIN_SKILL_NAME_REGEX.test(name)) {
    console.error(`Invalid skill name: ${name}`);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let res;
  try {
    res = await client.adminSkillInstall(name);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  // Anchor at the repo root so each harness discovers the skill under its own
  // skills/ dir; fall back to cwd outside a git repo. Use the locally-validated
  // `name` for the write dir (not the server-echoed res.name) — no trusted-server assumption.
  const root = findGitRoot() ?? process.cwd();

  // Unlike the PUBLIC skill — which the external `npx skills` installer fans
  // across every harness — the admin skill is private/backend-bundled, so THIS
  // command is the only install path and must carry the multi-harness fan-out
  // itself. Write into each recognized harness dir already present in the repo;
  // if none are present, write all of them so a non-Claude operator's harness
  // still discovers the skill.
  const present = HARNESS_SKILL_DIRS.filter((dir) => fs.existsSync(path.join(root, dir)));
  const targets = present.length > 0 ? present : HARNESS_SKILL_DIRS;

  // Same file set lands in every harness dir, so count once up front.
  const fileCount = Object.keys(res.files).length;
  const relDirs = targets.map((harness) => {
    const targetDir = path.join(root, harness, 'skills', name);
    writeFileMap(targetDir, res.files);
    return `${path.relative(root, targetDir)}/`;
  });

  console.log(`Installed skill "${name}" (${fileCount} file${fileCount === 1 ? '' : 's'}) → ${relDirs.join(', ')}`);
  console.log('Reload your agent (e.g. restart Claude Code) to pick up the skill.');
}

// ---------------------------------------------------------------------------
// Shared: scoped-target flags (--all | --org | --hub, --dry-run/--yes/--json)
//
// Used by every platform-admin fan-out subcommand; `opts.scopes` narrows the set
// for a command that has no hub form. The backend superRefine stays the
// authoritative validator; this only resolves the flags to its body shape.
// ---------------------------------------------------------------------------

interface ScopedTarget {
  body: { scope: 'all' | 'org' | 'hub'; org_id?: string; hub_id?: string; dry_run: boolean };
  targetLabel: string;
  dryRun: boolean;
  yes: boolean;
  jsonOutput: boolean;
}

interface ScopedTargetOptions {
  /** Scopes this subcommand accepts. A scope left out makes its flag an unknown flag. */
  scopes?: ReadonlyArray<'all' | 'org' | 'hub'>;
  /** What `--all` covers, for the confirm prompt ('ALL hubs' is not true of every command). */
  allLabel?: string;
}

function parseScopedTargetFlags(
  flagArgs: string[],
  usage: string,
  opts: ScopedTargetOptions = {},
): ScopedTarget {
  const scopes = opts.scopes ?? ['all', 'org', 'hub'];
  let all = false;
  let orgId: string | undefined;
  let hubId: string | undefined;
  let dryRun = false;
  let yes = false;
  let jsonOutput = false;

  const rejectUnknown = (arg: string): never => {
    console.error(`Unknown flag: ${arg}`);
    process.exit(1);
  };

  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    switch (arg) {
      case '--all': all = true; break;
      case '--dry-run': dryRun = true; break;
      case '--yes': yes = true; break;
      case '--json': jsonOutput = true; break;
      case '--org': orgId = requireFlagValue(flagArgs, ++i, '--org'); break;
      case '--hub':
        // An org-level command has no hub form; treat --hub as the typo it is
        // rather than silently sending a scope the backend will 400.
        if (!scopes.includes('hub')) rejectUnknown(arg);
        hubId = requireFlagValue(flagArgs, ++i, '--hub');
        break;
      default:
        rejectUnknown(arg);
    }
  }

  if ((all ? 1 : 0) + (orgId ? 1 : 0) + (hubId ? 1 : 0) !== 1) {
    const forms = scopes.map((s) => (s === 'all' ? '--all' : s === 'org' ? '--org <org_id>' : '--hub <hub_id>'));
    const oneOf = forms.length === 2
      ? forms.join(' or ')
      : `${forms.slice(0, -1).join(', ')}, or ${forms[forms.length - 1]}`;
    console.error(`Provide exactly ONE of ${oneOf}.`);
    console.error(usage);
    process.exit(1);
  }

  if (all) {
    const label = opts.allLabel ?? 'ALL hubs (platform-wide)';
    return { body: { scope: 'all', dry_run: dryRun }, targetLabel: label, dryRun, yes, jsonOutput };
  }
  if (orgId) {
    return { body: { scope: 'org', org_id: orgId, dry_run: dryRun }, targetLabel: `org ${orgId}`, dryRun, yes, jsonOutput };
  }
  return { body: { scope: 'hub', hub_id: hubId!, dry_run: dryRun }, targetLabel: `hub ${hubId}`, dryRun, yes, jsonOutput };
}

// ---------------------------------------------------------------------------
// Subcommands — Hub data repair
// ---------------------------------------------------------------------------

/**
 * `wayai admin hubs repair-team-orphans --all | --org <org_id> | --hub <hub_id>
 *   [--dry-run] [--yes] [--json]`
 *
 * Purges `hub_team_id` references left dangling by a team deletion that predates
 * the `deleteHubTeam` cascade (a `wayai push` / hub-sync that replaced a hub's
 * teams orphaned its memberships). A dangling membership resolves to an EMPTY
 * support scope, so its owner's Support list silently goes blank; a conversation
 * pinned to a deleted team goes invisible to everyone.
 *
 * Idempotent — a healthy hub reports zero and writes nothing, so re-running and
 * running platform-wide are both safe. Still write-y, so a non-dry run confirms.
 */
async function runRepairTeamOrphans(flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin hubs repair-team-orphans --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]';
  const { body, targetLabel, dryRun, yes, jsonOutput } = parseScopedTargetFlags(flagArgs, USAGE);

  if (!dryRun && !yes && !jsonOutput) {
    console.log(`About to PURGE dangling team references in: ${targetLabel}.`);
    console.log('Memberships pointing at a deleted team are removed (HubDO + UserDO mirror); conversations pinned to one are unassigned.');
    console.log('Run with --dry-run first to see what would change.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminRepairTeamOrphans(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;

  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.dry_run ? 'DRY RUN — nothing written' : 'repaired';
  console.log(
    `repair-team-orphans (${data.scope}) — ${mode}: ` +
    `hubs_scanned=${data.hubs_scanned} hubs_repaired=${data.hubs_repaired} ` +
    `team_users_removed=${data.team_users_removed}`,
  );
  // Partial-failure signal — the repair is idempotent, so re-running is safe.
  if (data.hubs_errored > 0 || data.orgs_errored > 0) {
    console.log(
      `WARNING: ${data.orgs_errored} org(s) and ${data.hubs_errored} hub(s) errored and were SKIPPED — re-run to cover them.`,
    );
  }
  if (data.details.length === 0) {
    console.log('(no dangling team references in scope)');
    return;
  }
  console.log('');
  console.log('hub_id | dangling_team_ids | team_users_removed');
  console.log('-------+-------------------+-------------------');
  for (const d of data.details) {
    console.log(`${d.hub_id} | ${d.dangling_team_ids.join(',')} | ${d.team_users_removed}`);
  }
}

/**
 * `wayai admin hubs repair-knowledge-blobs --all | --org <org_id> | --hub <hub_id>
 *   [--apply] [--yes] [--json]`
 *
 * The one-time sweep of the R2 objects retired hub knowledge left behind (wayai-pro/wayai#5142
 * step 5 C1): per hub, the keys under `{hub_id}/` no `file` row holds, older than the backend's
 * age window, eval attachments and call openings never included. A DRY RUN unless --apply is
 * given; --apply deletes, after a confirmation (--yes skips it).
 */
async function runRepairKnowledgeBlobs(flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin hubs repair-knowledge-blobs --all | --org <org_id> | --hub <hub_id> [--apply] [--yes] [--json]';
  const apply = flagArgs.includes('--apply');
  const { body, targetLabel, dryRun, yes, jsonOutput } = parseScopedTargetFlags(
    flagArgs.filter((arg) => arg !== '--apply'),
    USAGE,
  );
  if (apply && dryRun) {
    console.error('--apply and --dry-run contradict each other: a run is a dry run unless --apply is given.');
    console.error(USAGE);
    process.exit(1);
  }
  const input: AdminRepairKnowledgeBlobsInput = { ...body, dry_run: !apply };

  if (apply && !yes && !jsonOutput) {
    console.log(`About to DELETE the R2 objects no file row holds in: ${targetLabel}.`);
    console.log('Run without --apply first to see what would go.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminRepairKnowledgeBlobs(input);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.dry_run ? 'DRY RUN — nothing deleted' : 'deleted';
  console.log(
    `repair-knowledge-blobs (${data.scope}) — ${mode}: hubs_scanned=${data.hubs_scanned} ` +
    `keys_listed=${data.keys_listed} keys_unreferenced=${data.keys_unreferenced} keys_deleted=${data.keys_deleted}`,
  );
  if (data.hubs_errored > 0 || data.orgs_errored > 0) {
    console.log(
      `WARNING: ${data.orgs_errored} org(s) errored and were skipped, and ${data.hubs_errored} hub(s) stopped part-way ` +
      '(their finished pages are counted below) — re-run to finish them.',
    );
  }
  if (data.details.length === 0) {
    console.log('(no unreferenced keys in scope)');
    return;
  }
  console.log('');
  console.log('hub_id | listed | unreferenced | too_recent | deleted');
  console.log('-------+--------+--------------+------------+--------');
  for (const d of data.details) {
    const stopped = d.errored ? ' (stopped part-way: re-run to finish)' : '';
    console.log(`${d.hub_id} | ${d.keys_listed} | ${d.keys_unreferenced} | ${d.keys_too_recent} | ${d.keys_deleted}${stopped}`);
    for (const key of d.keys) console.log(`    ${key}`);
  }
}

/**
 * `wayai admin hubs repair-branching-orphans --all | --org <org_id> | --hub <hub_id>
 *   [--id <type>:<id> ...] [--dry-run] [--yes] [--json]`
 *
 * The #4577 remediation: overlapping preview → production syncs left a second copy of a
 * new entity in production that no mapping reaches, so no later publish or diff sees it.
 *
 * Without --id it only REPORTS twin candidates — unmapped rows sharing an identity with a
 * mapped row — and writes nothing. With --id (one production hub, so --hub) it deletes
 * exactly the named rows, each only after the backend verifies that no live preview maps
 * it and nothing in production references it; a row failing a check is skipped with its
 * reason. Name children before their parent (tool before agent) to remove both in one run.
 */
async function runRepairBranchingOrphans(flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin hubs repair-branching-orphans --all | --org <org_id> | --hub <hub_id> [--id <type>:<id> ...] [--dry-run] [--yes] [--json]';

  // `--id` repeats, so peel it off; every other flag goes through the shared scope parser.
  const targets: NonNullable<AdminRepairBranchingOrphansInput['targets']> = [];
  const scopeArgs: string[] = [];
  for (let i = 0; i < flagArgs.length; i++) {
    if (flagArgs[i] !== '--id') {
      scopeArgs.push(flagArgs[i]);
      continue;
    }
    const raw = requireFlagValue(flagArgs, ++i, '--id');
    const sep = raw.indexOf(':');
    const entityType = raw.slice(0, sep);
    const entityId = raw.slice(sep + 1);
    if (sep < 1 || !entityId || !(BRANCHING_ORPHAN_ENTITY_TYPES as readonly string[]).includes(entityType)) {
      console.error(`--id takes <type>:<id>, with type one of: ${BRANCHING_ORPHAN_ENTITY_TYPES.join(', ')}. Got: ${raw}`);
      process.exit(1);
    }
    targets.push({ entity_type: entityType as BranchingOrphanEntityType, entity_id: entityId });
  }

  const { body, targetLabel, dryRun, yes, jsonOutput } = parseScopedTargetFlags(scopeArgs, USAGE);
  if (targets.length > 0 && body.scope !== 'hub') {
    console.error('--id deletes rows of ONE production hub: combine it with --hub <hub_id>.');
    console.error(USAGE);
    process.exit(1);
  }

  if (targets.length > 0 && !dryRun && !yes && !jsonOutput) {
    console.log(`About to DELETE ${targets.length} named row(s) from production ${targetLabel}:`);
    for (const t of targets) console.log(`  ${t.entity_type}:${t.entity_id}`);
    console.log('Each goes only if no live preview maps it and nothing in production references it.');
    console.log('Run with --dry-run first to see what would change.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response: AdminRepairBranchingOrphansResponse;
  try {
    response = await client.adminRepairBranchingOrphans(targets.length > 0 ? { ...body, targets } : body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.mode === 'report'
    ? 'REPORT — nothing written'
    : data.dry_run ? 'DRY RUN — nothing written' : 'deleted';
  console.log(
    `repair-branching-orphans (${data.scope}) — ${mode}: ` +
    `hubs_scanned=${data.hubs_scanned} hubs_blocked=${data.hubs_blocked} ` +
    (data.mode === 'report' ? `twin_candidates=${data.twin_candidates}` : `rows_deleted=${data.rows_deleted}`),
  );
  if (data.hubs_errored > 0 || data.orgs_errored > 0) {
    console.log(
      `WARNING: ${data.orgs_errored} org(s) and ${data.hubs_errored} hub(s) errored and were SKIPPED — re-run to cover them.`,
    );
  }
  if (data.details.length === 0) {
    console.log('(no twin candidates in scope)');
    return;
  }

  for (const d of data.details) {
    console.log('');
    console.log(`hub ${d.hub_id} (live previews: ${d.live_preview_ids.join(', ') || 'none'})`);
    if (d.blocked) {
      console.log(`  BLOCKED: ${d.blocked} — no row on this hub can be verified, so none is deleted`);
    }
    for (const c of d.twin_candidates) {
      const refs = c.referenced_by.length > 0 ? `  referenced_by=${c.referenced_by.join(',')}` : '';
      console.log(`  ${c.entity_type}:${c.entity_id}  twin of ${c.twin_id}  [${c.identity}]${refs}`);
    }
    for (const t of d.targets) {
      const why = t.reason
        ? `  ${t.reason}` +
          (t.mapped_by.length > 0 ? ` by ${t.mapped_by.join(',')}` : '') +
          (t.referenced_by.length > 0 ? ` by ${t.referenced_by.join(',')}` : '') +
          (t.detail ? ` (${t.detail})` : '')
        : '';
      console.log(`  ${t.outcome.padEnd(12)} ${t.entity_type}:${t.entity_id}${why}`);
    }
  }
  if (data.mode === 'report' && data.twin_candidates > 0) {
    console.log('');
    console.log('Review each candidate, then delete it with: --hub <hub_id> --id <type>:<id> --dry-run, then --yes.');
  }
}

/**
 * `wayai admin orgs repair-legacy-grants --all | --org <org_id>
 *   [--dry-run] [--yes] [--json]`
 *
 * Repairs the org roster rows the platform-admin org-admin add wrote before
 * #3922 — the email landed in the `user_id` column with `user_email` NULL. Such
 * a row grants nothing (auth reads UserDO, never `member_grant`), still bills a
 * seat, and cannot self-heal: `resolveGrantByEmail` matches on `user_email`, and
 * `NULL = 'x@y.com'` is NULL. Each row either becomes real access or is dropped.
 *
 * Idempotent — a repaired org matches nothing on a re-run, so re-running and
 * --all are both safe. Still write-y, so a non-dry run confirms.
 */
async function runRepairLegacyOrgGrants(flagArgs: string[]): Promise<void> {
  const USAGE = 'wayai admin orgs repair-legacy-grants --all | --org <org_id> [--dry-run] [--yes] [--json]';

  // Org-level repair, so no --hub form.
  const scoped = parseScopedTargetFlags(flagArgs, USAGE, {
    scopes: ['all', 'org'],
    allLabel: 'ALL organizations (platform-wide)',
  });
  const { targetLabel, dryRun, yes, jsonOutput } = scoped;

  // Narrow to this endpoint's scope union, and keep `hub_id` out of a `.strict()`
  // body it would 400 on.
  const body: AdminRepairLegacyOrgGrantsInput = scoped.body.scope === 'org'
    ? { scope: 'org', org_id: scoped.body.org_id!, dry_run: dryRun }
    : { scope: 'all', dry_run: dryRun };

  if (!dryRun && !yes && !jsonOutput) {
    console.log(`About to REPAIR legacy org-admin roster rows in: ${targetLabel}.`);
    console.log('A row whose email resolves to a real user BECOMES REAL ACCESS (OrgDO roster + UserDO membership);');
    console.log('a row whose email matches no account is deleted. Neither sends the user an email.');
    console.log('Run with --dry-run first to see what would change.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminRepairLegacyOrgGrants(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;

  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.dry_run ? 'DRY RUN — nothing written' : 'repaired';
  console.log(
    `repair-legacy-grants (${data.scope}) — ${mode}: ` +
    `orgs_scanned=${data.orgs_scanned} rows_found=${data.rows_found} ` +
    `repaired=${data.rows_repaired} dropped=${data.rows_dropped} skipped=${data.rows_skipped}`,
  );
  // Partial-failure signal — the repair is idempotent, so re-running is safe.
  if (data.rows_errored > 0 || data.orgs_errored > 0) {
    console.log(
      `WARNING: ${data.orgs_errored} org(s) and ${data.rows_errored} row(s) errored and were SKIPPED — re-run to cover them.`,
    );
  }
  if (data.details.length === 0) {
    console.log('(no legacy email-keyed roster rows in scope)');
    return;
  }
  console.log('');
  console.log('org_id | user_email | action | user_id | reason');
  console.log('-------+------------+--------+---------+-------');
  for (const d of data.details) {
    console.log(`${d.org_id} | ${d.user_email} | ${d.action} | ${d.user_id ?? '-'} | ${d.reason ?? '-'}`);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Harness incident response (harness-agents PR 2.9b)
//
// The operator break-glass for runaway/compromised harness agents. `disable`
// flips the platform `harness_enabled` flag OFF so the per-turn kill switch
// aborts each harness turn on its next invocation; `mass-destroy` reaps every
// harness session / token / slot that is live RIGHT NOW. `enable` re-arms.
// Platform-admin gated; `mass-destroy` is DESTRUCTIVE (confirm required).
// ---------------------------------------------------------------------------

/**
 * `wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id>
 *   [--dry-run] [--yes] [--json]`
 *
 * Reaps every live harness session in scope. DESTRUCTIVE — requires an
 * interactive y/N confirm unless `--yes`, `--json`, or `--dry-run` is set.
 */
async function runHarnessMassDestroy(flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]';
  const { body, targetLabel, dryRun, yes, jsonOutput } = parseScopedTargetFlags(flagArgs, USAGE);

  // DESTRUCTIVE guard: a real reap needs explicit confirmation unless the caller
  // opted out via --yes or is scripting via --json.
  if (!dryRun && !yes && !jsonOutput) {
    console.log(`About to REAP every live harness session / token / slot in: ${targetLabel}.`);
    console.log('This destroys running harness sessions and revokes their tokens. Run with --dry-run first to see the blast radius.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminHarnessMassDestroy(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;

  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.dry_run ? 'DRY RUN — no reap performed' : 'reaped';
  console.log(
    `harness mass-destroy (${data.scope}) — ${mode}: ` +
    `hubs_scanned=${data.hubs_scanned} sessions_found=${data.sessions_found} ` +
    `sessions_reaped=${data.sessions_reaped} sandboxes_destroyed=${data.sandboxes_destroyed}`,
  );
  // Partial-failure signal: some hubs/sessions threw and were skipped. Surface it
  // loudly so the operator re-runs to cover them (the reap is idempotent).
  if (data.orgs_errored > 0 || data.hubs_errored > 0 || data.sessions_errored > 0) {
    console.log(
      `WARNING: ${data.orgs_errored} org(s), ${data.hubs_errored} hub(s) and ${data.sessions_errored} session(s) errored and were SKIPPED — re-run to cover them.`,
    );
  }
  if (data.details.length === 0) {
    console.log('(no live harness sessions in scope)');
    return;
  }
  console.log('');
  console.log('hub_id | conversation_id | sandbox_destroyed | reaped');
  console.log('-------+-----------------+-------------------+-------');
  for (const d of data.details) {
    console.log(
      `${d.hub_id} | ${d.conversation_id} | ${d.sandbox_destroyed} | ${d.reaped}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Platform gates — the `platform_config` 0/1 columns an operator flips from here
//
// Every gate's switch is ONE platform-wide column behind the same platform-admin-gated
// `GET`/`PATCH /api/admin/config` pair; the switch commands never take an org or hub. The
// descriptor keeps a gate's group name, its column and its operator-facing label
// together, so the three cannot drift apart across the call sites that need them.
// ---------------------------------------------------------------------------

/**
 * The `platform_config` columns this CLI flips, typed against the update contract:
 * renaming one there turns this into a narrower union and fails the build at the
 * descriptor below, rather than shipping a CLI that sends a key the route rejects.
 */
type PlatformGateKey = Extract<
  keyof UpdatePlatformConfigInput,
  'harness_enabled' | 'voice_calls_enabled' | 'threads_enabled' | 'communities_enabled'
>;

interface PlatformGate {
  /** The subcommand group: `wayai admin <group> enable|disable|status`. */
  group: string;
  /** The `platform_config` column this gate reads and writes. */
  key: PlatformGateKey;
  /** How the "platform-wide" flag rejection names this gate. */
  label: string;
  /** The gate's name at the head of the `enable|disable` output line. */
  displayName: string;
}

const HARNESS_GATE: PlatformGate = {
  group: 'harness',
  key: 'harness_enabled',
  label: 'harness rollout gate',
  displayName: 'Harness',
};

const VOICE_CALLS_GATE: PlatformGate = {
  group: 'voice-calls',
  key: 'voice_calls_enabled',
  label: 'voice-calls kill switch',
  displayName: 'Voice calls',
};

const THREADS_GATE: PlatformGate = {
  group: 'threads',
  key: 'threads_enabled',
  label: 'threads kill switch',
  displayName: 'Threads',
};

const COMMUNITIES_GATE: PlatformGate = {
  group: 'communities',
  key: 'communities_enabled',
  label: 'communities kill switch',
  displayName: 'Communities',
};

/**
 * A gate's switch is PLATFORM-wide, so `enable`/`disable`/`status` take no flags. Reject
 * anything passed so a stale `--org <id>` fails loudly instead of silently flipping the
 * platform flag.
 */
function rejectPlatformGateFlags(gate: PlatformGate, sub: string, flagArgs: string[]): void {
  if (flagArgs.length === 0) return;
  console.error(`Unknown flag: ${flagArgs[0]}`);
  console.error(`The ${gate.label} is platform-wide; there is no --org/--hub layer.`);
  console.error(`wayai admin ${gate.group} ${sub}`);
  process.exit(1);
}

/**
 * One gate's column as the API returned it. `undefined` means the response carried no
 * such column — distinct from a column that is present and 0.
 */
function readGateValue(data: Record<string, unknown>, key: PlatformGateKey): number | undefined {
  const value = data[key];
  return typeof value === 'number' ? value : undefined;
}

/** PATCH one gate's column and return the value the server echoed back. */
async function patchPlatformGate(gate: PlatformGate, enable: boolean): Promise<number | undefined> {
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    const res = await client.adminUpdateConfig({ [gate.key]: enable ? 1 : 0 });
    return readGateValue(res.data, gate.key);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }
}

/**
 * `wayai admin <group> status` — print one gate's current value. Shared by every gate:
 * an absent column reads 0/DISABLED, which is what each gate's fail-closed default does.
 * Returns the config row, so a gate with an org layer can print that layer from the
 * same read.
 */
async function runPlatformGateStatus(gate: PlatformGate, flagArgs: string[]): Promise<Record<string, unknown>> {
  rejectPlatformGateFlags(gate, 'status', flagArgs);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let data: Record<string, unknown>;
  try {
    data = (await client.adminGetConfig()).data;
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const value = readGateValue(data, gate.key) ?? 0;
  console.log(`${gate.key} (platform): ${value} (${value ? 'ENABLED' : 'DISABLED'})`);
  return data;
}

/**
 * `wayai admin <group> enable|disable` — flip one gate's switch (enable=true → 1,
 * false → 0) and print the line every gate shares. The caller prints its own follow-up.
 */
async function runPlatformGateToggle(gate: PlatformGate, enable: boolean, flagArgs: string[]): Promise<void> {
  rejectPlatformGateFlags(gate, 'enable|disable', flagArgs);

  const value = await patchPlatformGate(gate, enable);

  const state = enable ? 'ENABLED' : 'DISABLED';
  console.log(`${gate.displayName} ${state} platform-wide (${gate.key}=${value ?? (enable ? 1 : 0)}).`);
}

/**
 * Flip the harness rollout gate. One platform-wide flag: it both gates harness-agent
 * create/update and drives the per-turn kill switch.
 */
async function runHarnessToggle(enable: boolean, flagArgs: string[]): Promise<void> {
  await runPlatformGateToggle(HARNESS_GATE, enable, flagArgs);
  if (enable) {
    console.log('Harness agents may be created again and new harness turns may run.');
  } else {
    console.log('The per-turn kill switch now aborts every harness turn on its next invocation.');
    console.log('Next: run `wayai admin harness mass-destroy --all --dry-run` to see what is still live, then reap it.');
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Voice calls gate
//
// One layer, default-deny: the platform kill switch (`enable|disable`, through the
// shared platform-gate descriptor). While it is on, every org's hubs get voice-call
// surfaces once configured for them. Changes reach the gate once the platform-config
// cache is dropped, within about 60 s.
// ---------------------------------------------------------------------------

const PLATFORM_CONFIG_PROPAGATION_NOTE =
  'Takes effect within about 60 s (the platform-config cache is dropped; KV deletes propagate eventually).';

/** Flip the voice-calls kill switch. */
async function runVoiceCallsToggle(enable: boolean, flagArgs: string[]): Promise<void> {
  await runPlatformGateToggle(VOICE_CALLS_GATE, enable, flagArgs);
  if (enable) {
    console.log('Every org\'s hubs get voice-call surfaces once configured for them.');
  } else {
    console.log('Every voice-call surface is dark for every org.');
  }
  console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
}

/** `wayai admin voice-calls status` — the kill switch and the price, from one read. */
async function runVoiceCallsStatus(flagArgs: string[]): Promise<void> {
  const data = await runPlatformGateStatus(VOICE_CALLS_GATE, flagArgs);
  console.log(voiceCallPriceLine(data));
}

/**
 * The voice-call meter's price as the config row carries it: operations per connected
 * call-minute. Absent (a pre-migration row) reads 0, the default, as the meter reads it.
 */
function voiceCallPriceLine(data: Record<string, unknown>): string {
  const value = typeof data.voice_call_minute_ops === 'number' ? data.voice_call_minute_ops : 0;
  return `voice_call_minute_ops: ${value} operation(s) per connected call-minute${value === 0 ? ' (calls bill their turns only)' : ''}`;
}

/**
 * `wayai admin voice-calls price [--ops-per-minute <n>]` — print the voice-call meter's price,
 * or set it: the operations a call bills per connected minute of its voice agent, on top of
 * each call turn's initiation (owner decision 19, D5). Written through the platform config
 * PATCH, like the switch.
 */
async function runVoiceCallsPrice(flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin voice-calls price [--ops-per-minute <n>]';
  let raw: string | undefined;
  for (let i = 0; i < flagArgs.length; i++) {
    if (flagArgs[i] === '--ops-per-minute') {
      raw = requireFlagValue(flagArgs, i + 1, '--ops-per-minute');
      i++;
    } else {
      console.error(`Unknown flag: ${flagArgs[i]}`);
      console.error(usage);
      process.exit(1);
    }
  }
  // Digits only, so `1e1` and `0x6` are refused as typed; the range is the route's own schema.
  if (raw !== undefined && (!/^\d+$/.test(raw) || !voiceCallMinuteOps.safeParse(Number(raw)).success)) {
    console.error(`--ops-per-minute must be a whole number from 0 to ${MAX_VOICE_CALL_MINUTE_OPS}`);
    console.error(usage);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let data: Record<string, unknown>;
  try {
    data = raw === undefined
      ? (await client.adminGetConfig()).data
      : (await client.adminUpdateConfig({ voice_call_minute_ops: Number(raw) })).data;
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  console.log(voiceCallPriceLine(data));
  if (raw !== undefined) {
    console.log('Calls that end from now on are billed at this price.');
    console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Threads gate (gh #4661)
//
// The platform kill switch for contacts, direct messages and groups, through the shared
// platform-gate descriptor. Off, every /api/contacts route answers 404. It stays off until
// the program's web and unknown-email invites ship. Changes reach the gate once the
// platform-config cache is dropped, within about 60 s.
// ---------------------------------------------------------------------------

/** Flip the threads kill switch. */
async function runThreadsToggle(enable: boolean, flagArgs: string[]): Promise<void> {
  await runPlatformGateToggle(THREADS_GATE, enable, flagArgs);
  if (enable) {
    console.log('Contacts and threads are open to every signed-in user.');
  } else {
    console.log('Every contacts and threads route answers 404.');
  }
  console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
}

// ---------------------------------------------------------------------------
// Subcommands — Communities (gh #4872)
//
// The platform kill switch for the community member surface, through the shared platform-gate
// descriptor; the surface opens only while the threads switch is on too. And the platform
// admins' creation of an org-owned community, which works while the switch is off.
// ---------------------------------------------------------------------------

/** Flip the communities kill switch. */
async function runCommunitiesToggle(enable: boolean, flagArgs: string[]): Promise<void> {
  await runPlatformGateToggle(COMMUNITIES_GATE, enable, flagArgs);
  if (enable) {
    console.log('Members reach their communities while the threads switch (`wayai admin threads status`) is on too.');
  } else {
    console.log('No member reaches a community; platform admins still create them.');
  }
  console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
}

/** `wayai admin communities create --org <org_id> --name <name> [--json]` — a new org-owned community. */
async function runCommunitiesCreate(flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin communities create --org <org_id> --name <name> [--json]';
  let orgId: string | undefined;
  let name: string | undefined;
  let jsonOutput = false;
  for (let i = 0; i < flagArgs.length; i++) {
    const flag = flagArgs[i];
    if (flag === '--org') {
      orgId = requireFlagValue(flagArgs, i + 1, '--org');
      i++;
    } else if (flag === '--name') {
      name = requireFlagValue(flagArgs, i + 1, '--name');
      i++;
    } else if (flag === '--json') {
      jsonOutput = true;
    } else {
      console.error(`Unknown flag: ${flag}`);
      console.error(usage);
      process.exit(1);
    }
  }
  const parsed = adminCreateCommunityBody.safeParse({ org_id: orgId, name });
  if (!parsed.success) {
    console.error(parsed.error.issues.map((issue) => `--${issue.path[0] === 'org_id' ? 'org' : issue.path[0]}: ${issue.message}`).join('\n'));
    console.error(usage);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  let data: AdminCreateCommunityResponse['data'];
  try {
    data = (await client.adminCreateCommunity(parsed.data)).data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error(`Organization ${parsed.data.org_id} not found.`);
      process.exit(1);
    }
    if (err instanceof ApiError && err.status === 409) {
      console.error(conflictReasonOf(err) === COMMUNITY_REGISTRY_CLOSED_REASON
        ? `A deletion of organization ${parsed.data.org_id} closed it to new communities.`
        : `The organization already owns ${COMMUNITIES_PER_ORG_MAX} communities, the most it may.`);
      process.exit(1);
    }
    if (err instanceof ApiError && err.status === 503) {
      // A platform fault's 503 (gh #5551) can follow a community the backend created; running the
      // command again would create a second one.
      console.error(errorCodeOf(err.body) === 'TRANSIENT_STORAGE'
        ? `The platform was briefly unavailable, and the community may have been created. Check organization ${parsed.data.org_id}'s communities before running the command again.`
        : 'The community was not created in time. Run the command again.');
      process.exit(1);
    }
    exitOnApiError(err);
    throw err; // unreachable
  }
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  console.log(`Created community ${data.community_id} — "${data.name}", owned by ${data.org_name} (${data.org_id}).`);
}

/**
 * `wayai admin communities destroy <community_id> [--yes] [--json]` — destroy an org-owned
 * community. DESTRUCTIVE: confirms unless `--yes` or `--json`.
 */
async function runCommunitiesDestroy(positional: string[], flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin communities destroy <community_id> [--yes] [--json]';
  let yes = false;
  let jsonOutput = false;
  for (const flag of flagArgs) {
    if (flag === '--yes') {
      yes = true;
    } else if (flag === '--json') {
      jsonOutput = true;
    } else {
      console.error(`Unknown flag: ${flag}`);
      console.error(usage);
      process.exit(1);
    }
  }
  const parsed = communityIdParam.safeParse({ communityId: positional[0] });
  if (positional.length !== 1 || !parsed.success) {
    console.error('<community_id> must be one community id (a UUID).');
    console.error(usage);
    process.exit(1);
  }
  const { communityId } = parsed.data;
  if (!yes && !jsonOutput) {
    console.log(`About to destroy community ${communityId}: no one can read, join or leave it again,`);
    console.log("its members' lists drop it, and its storage is deleted. This cannot be undone.");
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  let data: AdminDestroyCommunityResponse['data'];
  try {
    data = (await client.adminDestroyCommunity(communityId)).data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error(`Community ${communityId} not found (never created, or already destroyed).`);
      process.exit(1);
    }
    exitOnApiError(err);
    throw err; // unreachable
  }
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  console.log(`Destroyed community ${data.community_id} — "${data.name}", owned by ${data.org_name} (${data.org_id}), at ${data.destroyed_at}.`);
}

// ---------------------------------------------------------------------------
// Subcommands — the WayAI Guide (wayai#4948)
//
// Which hub is the Guide (`platform_config.guide_hub_id`): every account provisioned while it is
// set is enrolled in it as an end user, and every signed-in person reaches it from the help
// entry. `set-hub` admits only a production chat hub with an enabled app channel (the server
// checks), and prints the hub's owning org — which pays for every Guide conversation — before it
// writes, unless `--yes` or `--json` skips the prompt. `clear` stops new enrollment and hides the
// help entry; people already enrolled keep their row.
// ---------------------------------------------------------------------------

/** One line naming a hub and its owning org. */
function describeGuideHub(hub: AdminGuideHub): string {
  return `${hub.hub_name ?? '(unnamed)'} (${hub.hub_id}), owned by ${hub.organization_name ?? '(unnamed org)'} (${hub.organization_id})`;
}

/** Parse the `allowed` boolean flags; exits on anything else. */
function parseGuideFlags(
  flagArgs: string[],
  usage: string,
  allowed: ReadonlyArray<'--yes' | '--json'>,
): { yes: boolean; jsonOutput: boolean } {
  for (const flag of flagArgs) {
    if (!(allowed as readonly string[]).includes(flag)) {
      console.error(`Unknown flag: ${flag}`);
      console.error(usage);
      process.exit(1);
    }
  }
  return { yes: flagArgs.includes('--yes'), jsonOutput: flagArgs.includes('--json') };
}

/** `wayai admin guide set-hub <hub_id> [--yes] [--json]` — make a hub the Guide. */
async function runGuideSetHub(positional: string[], flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin guide set-hub <hub_id> [--yes] [--json]';
  const { yes, jsonOutput } = parseGuideFlags(flagArgs, usage, ['--yes', '--json']);
  const parsed = adminSetGuideHubBody.safeParse({ hub_id: positional[0] });
  if (positional.length !== 1 || !parsed.success) {
    console.error('<hub_id> must be one hub id (a UUID).');
    console.error(usage);
    process.exit(1);
  }
  const hubId = parsed.data.hub_id;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const put = async (dryRun: boolean): Promise<AdminSetGuideHubResponse['data']> => {
    try {
      return (await client.adminSetGuideHub({ hub_id: hubId, dry_run: dryRun })).data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        console.error(`Hub ${hubId} not found (never created, or deleted).`);
        process.exit(1);
      }
      if (err instanceof ApiError && err.status === 409 && conflictReasonOf(err) === GUIDE_HUB_INELIGIBLE_REASON) {
        console.error(`Refused: ${extractApiMessage(err)}.`);
        process.exit(1);
      }
      exitOnApiError(err);
      throw err; // unreachable
    }
  };

  // Asked first, the server checks the hub and reports its owning org before anything is written;
  // with no prompt to show it, the write's own check is the one made.
  if (!yes && !jsonOutput) {
    const checked = await put(true);
    console.log(`The Guide will be ${describeGuideHub(checked.hub)}.`);
    console.log('Every account provisioned from now on is enrolled in it as an end user, and the');
    console.log('owning org pays for every Guide conversation.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }
  const written = await put(false);
  if (jsonOutput) {
    console.log(JSON.stringify(written, null, 2));
    return;
  }
  console.log(`The Guide is ${describeGuideHub(written.hub)}.`);
  console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
}

/** `wayai admin guide clear [--json]` — turn the Guide off. */
async function runGuideClear(positional: string[], flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin guide clear [--json]';
  if (positional.length > 0) {
    console.error(usage);
    process.exit(1);
  }
  const { jsonOutput } = parseGuideFlags(flagArgs, usage, ['--json']);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const { data } = await client.adminClearGuideHub();
    if (jsonOutput) {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }
  console.log('The Guide is off: nobody new is enrolled, and the help entry is hidden.');
  console.log('People already enrolled keep their row on the Guide hub.');
  console.log(PLATFORM_CONFIG_PROPAGATION_NOTE);
}

/** `wayai admin guide status [--json]` — which hub is the Guide, and its facts. */
async function runGuideStatus(positional: string[], flagArgs: string[]): Promise<void> {
  const usage = 'wayai admin guide status [--json]';
  if (positional.length > 0) {
    console.error(usage);
    process.exit(1);
  }
  const { jsonOutput } = parseGuideFlags(flagArgs, usage, ['--json']);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  let data;
  try {
    data = (await client.adminGetGuide()).data;
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  if (!data.guide_hub_id) {
    console.log('guide_hub_id (platform): not set — the Guide is off.');
    return;
  }
  console.log(`guide_hub_id (platform): ${data.guide_hub_id}`);
  if (!data.hub) {
    console.log('The hub cannot be read (deleted?). Run `wayai admin guide clear`.');
    return;
  }
  console.log(`Hub: ${describeGuideHub(data.hub)}`);
  console.log(`Environment: ${data.hub.hub_environment ?? 'unknown'}; type: ${data.hub.hub_type ?? 'unknown'}; app channel: ${data.hub.app_channel_enabled ? 'enabled' : 'DISABLED'}`);
}

/** The `details.reason` a 409's body carries, or null. */
function conflictReasonOf(err: ApiError): string | null {
  try {
    const reason = (JSON.parse(err.body) as { details?: { reason?: unknown } }).details?.reason;
    return typeof reason === 'string' ? reason : null;
  } catch {
    return null;
  }
}

/** Read the value that follows a value-flag; exit with a clear message if absent. */
function requireFlagValue(flagArgs: string[], index: number, flag: string): string {
  const v = flagArgs[index];
  // Reject an empty string too, not just undefined/`--`-prefixed. An empty flag value
  // is a user error everywhere, and a falsy value read back as "flag not given" would
  // silently widen a scoped command (e.g. `mass-destroy --org ""`) instead of failing.
  if (v === undefined || v === '' || v.startsWith('--')) {
    console.error(`${flag} requires a value`);
    process.exit(1);
  }
  return v;
}

/** The backend's error code (`AppError.toJSON()`'s `error`) in a response body, or null. */
function errorCodeOf(body: string): string | null {
  try {
    const code = (JSON.parse(body) as { error?: unknown })?.error;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}

function exitOnApiError(err: unknown): void {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      console.error('Authentication failed. Run `wayai login` again.');
      process.exit(1);
    }
    if (err.status === 403) {
      console.error('Access denied. `wayai admin` requires platform-admin access.');
      console.error('Run `wayai whoami` to check your identity and platform-admin status.');
      process.exit(1);
    }
    if (err.status === 404) {
      console.error('Not found.');
      process.exit(1);
    }
    if (err.status === 429) {
      console.error('Rate limited. `wayai admin` is capped at 120 reads/hour per user.');
      process.exit(1);
    }
    if (err.status === 503) {
      // A retryable cold-start (CLICKHOUSE_COLD) that outlived the ApiClient's
      // bounded retry is transient, NOT the kill switch — mislabeling it sends
      // an operator down the wrong path (gh #2883).
      if (err.isRetryable) {
        console.error('ClickHouse is warming up (cold start); the read is temporarily unavailable. Retry in a few seconds.');
      } else if (errorCodeOf(err.body) === 'TRANSIENT_STORAGE') {
        // A Durable Object's platform fault, which every `/api/*` route answers as this 503 (gh #5551):
        // a deploy in progress, not the kill switch.
        console.error('The platform is briefly unavailable (usually a deploy in progress). Retry in a few seconds.');
      } else {
        console.error('Admin debug reads are currently disabled (platform kill switch).');
      }
      process.exit(1);
    }
    if (err.status === 400) {
      console.error(`Bad request: ${err.body || err.message}`);
      process.exit(1);
    }
    console.error(err.message);
    process.exit(1);
  }
  console.error(err instanceof Error ? err.message : String(err));
  process.exit(1);
}

/**
 * Render the standard debug rows result: --json passes through the raw payload;
 * default mode prints the table + footer with row count, truncation marker, and
 * the redacted-columns summary.
 */
function printDebugRowsResult(
  data: {
    rows: Array<Record<string, unknown>>;
    redacted_columns: string[];
    truncated: boolean;
    limit: number;
  },
  jsonOutput: boolean,
): void {
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  if (data.rows.length === 0) {
    console.log('(no rows)');
    if (data.redacted_columns.length > 0) {
      console.log(`\nredacted columns: ${data.redacted_columns.join(', ')}`);
    }
    return;
  }

  printRowsTable(data.rows);
  console.log('');
  console.log(`rows: ${data.rows.length}${data.truncated ? ` (truncated to ${data.limit})` : ''}`);
  if (data.redacted_columns.length > 0) {
    console.log(`redacted columns: ${data.redacted_columns.join(', ')}`);
  }
}

/**
 * Pretty-print rows as a columnar text table. Columns are the union of keys
 * across all rows, ordered by first appearance. Values are coerced to strings
 * and truncated at a per-column width so a single row with a huge blob doesn't
 * blow up the whole table.
 */
function printRowsTable(rows: Array<Record<string, unknown>>): void {
  const COL_MAX = 80;
  const cols: string[] = [];
  for (const row of rows) for (const k of Object.keys(row)) if (!cols.includes(k)) cols.push(k);

  const widths: Record<string, number> = {};
  for (const c of cols) widths[c] = Math.min(COL_MAX, Math.max(c.length, ...rows.map((r) => formatValue(r[c]).length)));

  const header = cols.map((c) => c.padEnd(widths[c])).join(' | ');
  const divider = cols.map((c) => '-'.repeat(widths[c])).join('-+-');
  console.log(header);
  console.log(divider);
  for (const row of rows) {
    console.log(cols.map((c) => truncate(formatValue(row[c]), widths[c]).padEnd(widths[c])).join(' | '));
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, Math.max(1, n - 1)) + '…' : s;
}

// ---------------------------------------------------------------------------
// Subcommands — Report (platform_report triage queue)
//
// Consumed by Claude Code Desktop's 15-min triage routine and by humans
// running ad-hoc triage. Mutations (group/escalate/addressed/dismiss) drive
// the state machine in PlatformDO, which is the sole owner of email side
// effects (escalation, resolution, dismissal — `cli` (human) rows only).
// ---------------------------------------------------------------------------

/**
 * A value flag's value from `parseReportFlags`, or undefined when it was not given; given with no
 * value (read as 'true') it is a user error.
 */
function flagValue(flags: Record<string, string>, name: string): string | undefined {
  const value = flags[name];
  if (value === 'true') {
    console.error(`--${name} requires a value`);
    process.exit(1);
  }
  return value;
}

/** Minutes since `iso`, for a list's age column. */
function minutesAgo(iso: string): number {
  return Math.floor((Date.now() - Date.parse(iso)) / 1000 / 60);
}

function parseReportFlags(flagArgs: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < flagArgs.length; i++) {
    const flag = flagArgs[i];
    if (!flag.startsWith('--')) continue;
    const key = flag.slice(2);
    const next = flagArgs[i + 1];
    if (next === undefined || next.startsWith('--')) {
      out[key] = 'true';
    } else {
      out[key] = next;
      i++;
    }
  }
  return out;
}

async function runReportList(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    const result = await client.listAdminReports({
      status: flags.status,
      source: flags.source,
      limit: flags.limit ? Number(flags.limit) : undefined,
      offset: flags.offset ? Number(flags.offset) : undefined,
      since: flags.since,
      resolved_since: flags['resolved-since'],
    });

    if (flags.json === 'true') {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(`Reports: ${result.data.length}/${result.total}`);
    for (const r of result.data) {
      const age = minutesAgo(r.created_at);
      const meta = [
        r.source,
        r.status,
        // Surface a broken Sentry reference (set at escalation) — these never auto-resolve.
        r.sentry_ref_status && r.sentry_ref_status !== 'valid' ? `sentry_ref:${r.sentry_ref_status}` : null,
        r.gh_issue_url ? `gh:${r.gh_issue_url}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      console.log(`  ${r.report_id}  [${meta}]  ${age}m ago`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportShow(positional: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin report show <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.getAdminReport(reportId);
    console.log(JSON.stringify(result.data, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportGroup(positional: string[]): Promise<void> {
  const [canonicalId, ...memberIds] = positional;
  if (!canonicalId || memberIds.length === 0) {
    console.error('wayai admin report group <canonical_id> <member_id> [<member_id> ...]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.groupAdminReports(canonicalId, memberIds);
    console.log(`Grouped ${result.grouped} member(s) under ${result.canonical_id}`);
    for (const s of result.skipped) {
      const reason =
        s.reason === 'different_gh_issue'
          ? `linked to a different GitHub issue (${s.detail})`
          : s.reason === 'grouped_elsewhere'
            ? `already grouped under ${s.detail}`
            : s.reason === 'terminal'
              ? `already ${s.detail} (terminal)`
              : 'not found';
      console.log(`  skipped ${s.report_id} — ${reason}`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportUngroup(positional: string[]): Promise<void> {
  const memberId = positional[0];
  if (!memberId) {
    console.error('wayai admin report ungroup <member_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.ungroupAdminReport(memberId);
    console.log(`Detached ${result.report.report_id} → ${result.report.status}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportEscalate(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const ghUrl = flags.gh;
  if (!reportId || !ghUrl) {
    console.error('wayai admin report escalate <report_id> --gh <github_issue_url> [--verdict <text>]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.transitionAdminReport(reportId, {
      target: 'escalated',
      gh_issue_url: ghUrl,
      ai_verdict: flags.verdict,
    });
    console.log(`Report ${result.report.report_id} → ${result.report.status}`);
    // The backend cross-checks a sentry-sourced report's captured reference
    // against the live Sentry issue at escalation. A broken ref still escalates
    // (the bug may be real) but won't auto-resolve on close — flag it loudly.
    const refStatus = result.report.sentry_ref_status;
    if (refStatus === 'not_found') {
      console.log(`  ⚠ Sentry reference not_found — the cited issue does not exist on Sentry. Auto-resolve on close is skipped; verify the reference.`);
    } else if (refStatus === 'mismatch') {
      console.log(`  ⚠ Sentry reference mismatch — issue_id resolves but its short_id ≠ the captured one. Auto-resolve on close is skipped.`);
    } else if (refStatus === 'unverified') {
      console.log(`  ℹ Sentry reference unverified — could not reach Sentry (no token / API error). Auto-resolve on close is skipped.`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportShipped(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  if (!reportId) {
    console.error('wayai admin report shipped <report_id> [--note <what shipped, for the reporter>]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    // `--note` is a value-flag; if omitted, parseReportFlags leaves it undefined and the
    // backend records a default. Don't pass the literal "true" from a value-less `--note`.
    const note = flags.note && flags.note !== 'true' ? flags.note : undefined;
    const result = await client.transitionAdminReport(reportId, { target: 'shipped', note });
    console.log(`Report ${result.report.report_id} → ${result.report.status} (awaiting reporter verification)`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportAddressed(positional: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin report addressed <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.transitionAdminReport(reportId, { target: 'addressed' });
    console.log(`Report ${result.report.report_id} → ${result.report.status}`);
    if (result.cascaded_report_ids.length > 0) {
      console.log(`Cascaded ${result.cascaded_report_ids.length} grouped member(s).`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportDismiss(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const reason = flags.reason;
  if (!reportId || !reason) {
    console.error('wayai admin report dismiss <report_id> --reason <text> [--verdict <text>] [--final]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    // `--final` makes the dismissal non-contestable (the reporter can't dispute it). Use it to
    // settle a clearly-wrong reporter without waiting for the contest cap.
    const result = await client.transitionAdminReport(reportId, {
      target: 'dismissed',
      dismissal_reason: reason,
      ai_verdict: flags.verdict,
      final: flags.final === 'true',
    });
    console.log(`Report ${result.report.report_id} → ${result.report.status}${flags.final === 'true' ? ' (final — non-contestable)' : ''}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportHold(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const reason = flags.reason;
  // Reason is mandatory: a hold with no recorded rationale defeats the whole point (an
  // auditable, dateable record of *why* this row is waiting another cycle).
  if (!reportId || !reason || reason === 'true') {
    console.error('wayai admin report hold <report_id> --reason <text>');
    console.error('Defers a pending report one triage cycle (records a note + bumps hold_count). Silent — no email.');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.holdAdminReport(reportId, reason);
    console.log(
      `Report ${result.report.report_id} held (cycle ${result.report.hold_count}) — remains ${result.report.status}.`,
    );
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportUpdate(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  // Use the dedicated content-flag parser (not parseReportFlags): it errors on a
  // value-less flag and keeps an explicit "" as a clear, so a malformed
  // `--error` can't silently persist the literal "true" into a content field.
  const { patch, error } = parseReportEditArgs(flagArgs);

  if (error) {
    console.error(error);
    process.exit(1);
  }
  if (!reportId || Object.keys(patch).length === 0) {
    console.error('wayai admin report update <report_id> [--title <t>] [--description <d>] [--error <e>] [--steps <s>] [--context <c>]');
    console.error('At least one field to change is required. Pass "" to clear error/steps/context.');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.updateAdminReport(reportId, patch);
    console.log(`Report ${result.report_id} content updated (status: ${result.status}).`);
    if (result.status === 'escalated') {
      console.log('Note: this edit is queue-local — the linked GitHub issue body is NOT updated.');
    }
    console.log(JSON.stringify(result.raw_payload, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — People (moderation of person-to-person messaging)
//
// The team's answer to a person's report (App Store guideline 1.2: act within 24 hours).
// `list/show/user` are reads; `remove-message/suspend/unsuspend/dismiss` act, and the backend
// records each action — who, what, when, and the report it answers — once it is done.
// ---------------------------------------------------------------------------

async function runPeopleList(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const since = flagValue(flags, 'since');
  const limit = flags.limit === undefined ? undefined : parsePositiveIntFlag('--limit', flagValue(flags, 'limit'));
  const offset = flagValue(flags, 'offset');
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.listAdminPeopleReports({
      pending: flags.pending === 'true' ? 'true' : undefined,
      since,
      limit,
      offset: offset ? Number(offset) : undefined,
    });
    if (flags.json === 'true') {
      console.log(JSON.stringify(result, null, 2));
      return;
    }
    console.log(`People reports: ${result.data.length}/${result.total}`);
    for (const r of result.data) {
      const age = minutesAgo(r.created_at);
      const state = r.action_count === 0 ? 'pending' : `${r.action_count} action${r.action_count === 1 ? '' : 's'}`;
      const about = `${r.reported_user_id ? `  reported:${r.reported_user_id}` : ''}${r.community_id ? `  community:${r.community_id}` : ''}`;
      console.log(`  ${r.report_id}  [${r.reason} · ${r.target} · ${state}]${about}  ${age}m ago`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runPeopleShow(positional: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin people show <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.getAdminPeopleReport(reportId);
    console.log(JSON.stringify(result.data, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runPeopleUser(positional: string[]): Promise<void> {
  const userId = positional[0];
  if (!userId) {
    console.error('wayai admin people user <user_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.getAdminPeopleUser(userId);
    console.log(JSON.stringify(result.data, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

/** One line for a recorded action, and one more when it changed nothing. */
function printPeopleAction(response: AdminPeopleModerationResponse, unchanged?: string): void {
  const { record, changed } = response.data;
  console.log(`Recorded ${record.action} ${record.action_id} at ${record.created_at}.`);
  if (!changed && unchanged) console.log(unchanged);
}

async function runPeopleDismiss(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin people dismiss <report_id> [--note <text>]');
    process.exit(1);
  }
  const note = flagValue(parseReportFlags(flagArgs), 'note');
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    printPeopleAction(await client.dismissAdminPeopleReport(reportId, { note }));
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runPeopleRemoveMessage(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin people remove-message <report_id> [--message <message_id>] [--note <text>] [--yes]');
    process.exit(1);
  }
  const flags = parseReportFlags(flagArgs);
  const messageId = flagValue(flags, 'message');
  const note = flagValue(flags, 'note');
  if (flags.yes !== 'true') {
    console.log(`About to delete ${messageId ? `message ${messageId}` : 'the reported message'} of report ${reportId} for everyone in its thread or community.`);
    console.log('Its text and file are erased; this cannot be undone.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    printPeopleAction(
      await client.removeAdminPeopleMessage({ report_id: reportId, message_id: messageId, note }),
      'The message was already deleted.',
    );
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runPeopleSuspension(suspend: boolean, positional: string[], flagArgs: string[]): Promise<void> {
  const userId = positional[0];
  if (!userId) {
    console.error(suspend
      ? 'wayai admin people suspend <user_id> [--report <report_id>] [--note <text>]'
      : 'wayai admin people unsuspend <user_id> [--note <text>]');
    process.exit(1);
  }
  const flags = parseReportFlags(flagArgs);
  const note = flagValue(flags, 'note');
  const reportId = suspend ? flagValue(flags, 'report') : undefined;
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    if (suspend) {
      printPeopleAction(await client.suspendAdminPeopleUser(userId, { report_id: reportId, note }), 'The account was already suspended.');
      console.log('It can no longer send messages or invites, accept invites, or create or change groups; in communities it cannot post, join, moderate or create rooms. Nobody else is told.');
    } else {
      printPeopleAction(await client.unsuspendAdminPeopleUser(userId, { note }), 'The account was not suspended.');
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Notices (platform broadcasts, feed 2)
// ---------------------------------------------------------------------------

const VALID_NOTICE_SEVERITIES = ['critical', 'warn', 'info'] as const satisfies readonly NoticeSeverity[];

async function runNoticeCreate(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const title = flags.title;
  if (!title || title === 'true') {
    console.error('wayai admin notice create --title <text> [--severity critical|warn|info] [--scope <scope>] [--body <text>] [--link <url>] [--starts <ISO>] [--ends <ISO>]');
    console.error('Authors a platform broadcast (feed 2). Severity defaults to info, scope to global.');
    process.exit(1);
  }
  const severity = (flags.severity ?? 'info') as NoticeSeverity;
  if (!VALID_NOTICE_SEVERITIES.includes(severity)) {
    console.error(`--severity must be one of: ${VALID_NOTICE_SEVERITIES.join(', ')}`);
    process.exit(1);
  }
  // Guard the value-less-flag sentinel (parseReportFlags maps a bare `--scope` to 'true')
  // so it fails locally with a clear message instead of as a backend regex 400.
  if (flags.scope === 'true') {
    console.error('--scope requires a value (e.g. global, provider:gemini, service:analytics)');
    process.exit(1);
  }

  const body: CreateNoticeBody = {
    severity,
    scope: flags.scope ?? 'global',
    title,
    ...(flags.body && flags.body !== 'true' ? { body: flags.body } : {}),
    ...(flags.link && flags.link !== 'true' ? { link: flags.link } : {}),
    ...(flags.starts && flags.starts !== 'true' ? { starts_at: flags.starts } : {}),
    ...(flags.ends && flags.ends !== 'true' ? { ends_at: flags.ends } : {}),
  };

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.createAdminNotice(body);
    const n = result.data;
    console.log(`Created notice ${n.notice_id} — [${n.severity} · ${n.scope}] ${n.title}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runNoticeList(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.listAdminNotices({
      status: flags.status,
      limit: flags.limit ? Number(flags.limit) : undefined,
      offset: flags.offset ? Number(flags.offset) : undefined,
    });

    if (flags.json === 'true') {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(`Notices: ${result.data.length}/${result.total}`);
    for (const n of result.data) {
      const meta = [n.severity, n.scope, n.status, n.ends_at ? `ends:${n.ends_at}` : null]
        .filter(Boolean)
        .join(' · ');
      console.log(`  ${n.notice_id}  [${meta}]  ${n.title}`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runNoticeResolve(positional: string[]): Promise<void> {
  const noticeId = positional[0];
  if (!noticeId) {
    console.error('wayai admin notice resolve <notice_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.resolveAdminNotice(noticeId);
    console.log(`Notice ${result.data.notice_id} resolved.`);
  } catch (err) {
    exitOnApiError(err);
  }
}

function printHelp(): void {
  console.error(`
wayai admin — platform-admin diagnostic commands

Usage:
  wayai admin do tables <type> <id>
  wayai admin do read <type> <id> <table> [--limit N] [--id X] [--json]
  wayai admin do counts <hub|org> <id> [--json]    Numbers, no text: a hub's toolset connections, knowledge placeholders per stored
                                                   text, secret columns' shapes and (production) publish state; an org's
                                                   skills, hub links, moved-knowledge registry and credentials' shapes

  wayai admin analytics tables
  wayai admin analytics read <table> [--conversation-id X] [--hub-id Y] [--message-id Z] [--limit N] [--json]

  wayai admin archive <hub_id> <conversation_id> [--message-id X] [--json]

  wayai admin observability <hub_id> <conversation_id> [--message-id X] [--json]

  wayai admin report list [--status STATUS] [--source SOURCE] [--limit N] [--offset N] [--since ISO] [--resolved-since ISO] [--json]
                          --since filters/orders on created_at (when it was filed);
                          --resolved-since on resolved_at (what we resolved recently —
                          selects terminal rows on its own, no --status needed).
  wayai admin report show <report_id>
  wayai admin report group <canonical_id> <member_id> [<member_id> ...]
  wayai admin report ungroup <member_id>
  wayai admin report escalate <report_id> --gh <github_issue_url> [--verdict <text>]
  wayai admin report shipped <report_id> [--note <what shipped, for the reporter>]
  wayai admin report addressed <report_id>
  wayai admin report dismiss <report_id> --reason <text> [--verdict <text>] [--final]
  wayai admin report hold <report_id> --reason <text>
  wayai admin report update <report_id> [--title <t>] [--description <d>] [--error <e>] [--steps <s>] [--context <c>]

  wayai admin people list [--pending] [--since ISO] [--limit N] [--offset N] [--json]
  wayai admin people show <report_id>
  wayai admin people remove-message <report_id> [--message <message_id>] [--note <text>] [--yes]
  wayai admin people suspend <user_id> [--report <report_id>] [--note <text>]
  wayai admin people unsuspend <user_id> [--note <text>]
  wayai admin people dismiss <report_id> [--note <text>]
  wayai admin people user <user_id>

  wayai admin notice create --title <text> [--severity critical|warn|info] [--scope <scope>] [--body <text>] [--link <url>] [--starts <ISO>] [--ends <ISO>]
  wayai admin notice list [--status active|resolved] [--limit N] [--offset N] [--json]
  wayai admin notice resolve <notice_id>

  wayai admin skill install [name]                 Install a private operator skill (default: wayai-admin) into each present harness's skills/ dir

  wayai admin hubs repair-team-orphans --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]
                                                   Purge hub_team_id references left dangling by a pre-cascade team deletion (silently empties a support agent's queue). Idempotent — run --dry-run first

  wayai admin hubs repair-branching-orphans --all | --org <org_id> | --hub <hub_id> [--id <type>:<id> ...] [--dry-run] [--yes] [--json]
                                                   Without --id: report unmapped production rows that duplicate a mapped one (#4577). With --id: delete exactly those rows of one production hub, each only if no live preview maps it and nothing references it

  wayai admin hubs repair-knowledge-blobs --all | --org <org_id> | --hub <hub_id> [--apply] [--yes] [--json]
                                                   Report, or with --apply delete, the R2 objects under each hub no file row holds (retired knowledge's, #5142), older than a day; never eval attachments or call openings. A dry run by default. Run it once the deploy that retired knowledge is fully live: a hub on older code stops on its first page, as errored

  wayai admin orgs repair-legacy-grants --all | --org <org_id> [--dry-run] [--yes] [--json]
                                                   Repair org-admin roster rows keyed by email with a NULL user_email (pre-#3922 platform-admin adds): grant the real access they never conferred, or drop the phantom seat. Idempotent — run --dry-run first

  wayai admin harness disable                      Flip the platform harness_enabled flag OFF (break-glass: blocks harness-agent writes and the per-turn kill switch aborts each harness turn on its next invocation)
  wayai admin harness enable                       Flip the platform harness_enabled flag ON
  wayai admin harness status                       Print the platform harness_enabled flag
  wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]
                                                   Reap every live harness session/token/slot in scope (run --dry-run first to see the blast radius)

  wayai admin voice-calls enable                   Flip the platform voice_calls_enabled kill switch ON (every org)
  wayai admin voice-calls disable                  Flip it OFF: every voice-call surface goes dark for every org
  wayai admin voice-calls status                   Print the kill switch and the call price
  wayai admin voice-calls price [--ops-per-minute <n>]
                                                   Print, or set, the operations a call bills per connected minute (default 0)

  wayai admin threads enable                       Flip the platform threads_enabled kill switch ON (contacts, direct messages, groups)
  wayai admin threads disable                      Flip it OFF: every /api/contacts route answers 404
  wayai admin threads status                       Print the kill switch

  wayai admin communities enable                   Flip the platform communities_enabled kill switch ON (members reach communities while threads is ON too)
  wayai admin communities disable                  Flip it OFF: no member reaches a community
  wayai admin communities status                   Print the kill switch
  wayai admin communities create --org <org_id> --name <name> [--json]
                                                   Create a community owned by the org (at most ${COMMUNITIES_PER_ORG_MAX} per org); works while the switch is OFF
  wayai admin communities destroy <community_id> [--yes] [--json]
                                                   Destroy a community: its members' lists drop it and its storage is deleted (DESTRUCTIVE; confirms unless --yes)

  wayai admin guide set-hub <hub_id> [--yes] [--json]
                                                   Make a production chat hub with an enabled app channel the WayAI Guide (prints its owning org and confirms, unless --yes or --json)
  wayai admin guide clear [--json]                 Turn the Guide off: nobody new is enrolled and the help entry is hidden
  wayai admin guide status [--json]                Print which hub is the Guide, and its facts

Sources:
  do             Live DO SQLite (existing conversation, hub config, etc.).
  analytics      ClickHouse projections (conversation, message, schedule_event).
                 \`message\` is per-message usage facts and INCLUDES background agent
                 roles, as the conversation figures now do — so its unfiltered sums
                 REPRODUCE them, and filtering those roles out lands at or below
                 (equal when the conversation ran no background agent).
  archive        R2 archive blob — canonical content for ended conversations after DO cleanup.
  observability  R2 LLM observability — per-message OTel GenAI records (prompts, completions,
                 tool calls, tokens, latency). Default lists all records for the conversation;
                 --message-id drills into one. Use this admin variant when the caller has no
                 hub access; otherwise prefer \`wayai conversations <id> observability\`.
  report         platform_report triage queue. \`list/show\` are reads; \`group/ungroup/
                 escalate/addressed/dismiss\` drive the state machine (which owns email
                 fanout for cli (human) rows). \`ungroup\` detaches a grouped member back
                 to pending (inverse of \`group\`). \`hold\` defers a pending row one cycle
                 (records an internal note + bumps hold_count) without transitioning it —
                 silent for every source. \`update\` amends content fields
                 (title/description/error/steps/context) while pending/escalated — the
                 dedup fingerprint stays frozen and no email fires. Agent/Sentry rows
                 never trigger emails.
  people         Moderation of person-to-person messaging and communities: the reports people
                 file from the app (each also emailed to the platform admins) and the team's
                 answer, due within 24 hours (App Store guideline 1.2). \`list\` is newest first
                 with each report's action count (--pending: none yet); \`show\` prints one with
                 the messages its reporter saw and its actions. \`remove-message\` deletes, for
                 everyone, the report's message — or --message, another of its thread's or
                 community's. \`suspend\` stops an account reaching anyone on the people surface:
                 no messages, invites, accepted invites, new groups or group changes, and in
                 communities no posts, joins, moderation or new rooms; it can still read, delete
                 its own messages, leave, block and report, and nobody else is told. \`unsuspend\` lifts it; \`user\` prints an account's suspension and
                 the actions on it. \`dismiss\` records a report reviewed with nothing to do.
                 Each action is recorded with who took it and when; repeating one changes
                 nothing and records it again.
  notice         platform_notice broadcasts (Status & Notices feed 2) — operator-authored
                 "Gemini degraded" / "Analytics delayed" notices. \`create\` authors one
                 (severity defaults to info, scope to global); \`list\` reads them
                 (--status active|resolved); \`resolve\` ends an active notice.
  hubs           Hub data repair. \`repair-team-orphans\` purges hub_team_id references left
                 dangling by a team deletion that predates the deleteHubTeam cascade (a
                 \`wayai push\` / hub-sync that replaced a hub's teams orphaned its
                 memberships). A dangling membership resolves to an EMPTY support scope, so
                 its owner's Support list silently goes blank; a conversation pinned to a
                 deleted team goes invisible to everyone. Idempotent — a healthy hub writes
                 nothing, so re-running and --all are both safe.
                 \`repair-branching-orphans\` handles the second copies overlapping publishes
                 left in production (#4577): no mapping reaches them, so no later publish or
                 diff sees them. It never picks rows itself — a deleted preview leaves live
                 rows unmapped too — so without --id it only reports candidates, and with
                 --id it deletes the named rows that pass its checks.
                 \`repair-knowledge-blobs\` sweeps the R2 objects retired hub knowledge left
                 behind once its rows were deleted: keys no file row holds, older than a
                 day. It reports by default and deletes only with --apply.
  orgs           Org roster repair. \`repair-legacy-grants\` fixes member_grant rows the
                 platform-admin org-admin add wrote before #3922: the email landed in the
                 user_id column with user_email NULL. Auth reads UserDO, never member_grant,
                 so the row grants NOTHING — while still counting as a seat, which refuses
                 the org's next legitimate admin add with SEAT_QUOTA_EXCEEDED. It cannot
                 self-heal (resolveGrantByEmail matches on user_email, and NULL = 'x@y.com'
                 is NULL). Each row is resolved to a real user and granted both halves of
                 access, or dropped when no account matches. A repaired row is a REAL grant
                 to a real person and sends no email, so read --dry-run output as the list of
                 people about to get access. Idempotent.

  harness        Incident response for harness agents. \`disable\`/\`enable\` flip the single
                 PLATFORM-wide harness_enabled flag; \`status\` prints it. That one flag both
                 gates harness-agent create/update (default-deny, fail-closed) and drives
                 the per-turn kill switch, so \`disable\` is the break-glass: each harness
                 turn aborts on its next invocation. \`mass-destroy\` reaps every harness
                 session/token/slot that is live NOW (scope: --hub < --org < --all, prefer
                 the narrowest). Run \`mass-destroy --dry-run\` first to see the blast
                 radius.
  voice-calls    The voice-calls gate, default-deny: the PLATFORM-wide voice_calls_enabled
                 switch (\`enable\`/\`disable\`). While it is on, every org's hubs get
                 voice-call surfaces once configured for them; \`disable\` darkens every
                 org. \`price\` is the call meter's price in operations per connected
                 call-minute (0 until set). Changes land within about 60 s.
  threads        The threads gate, default-deny: the PLATFORM-wide threads_enabled switch
                 (\`enable\`/\`disable\`) for contacts, direct messages and groups between
                 people. Off, every /api/contacts route answers 404. Changes land within
                 about 60 s.
  communities    Org-owned communities. \`enable\`/\`disable\` flip the PLATFORM-wide
                 communities_enabled switch, default-deny and nested in threads: members
                 reach a community only while both switches are on. \`create\` makes a
                 community for an org and \`destroy\` removes one, whichever way the switch
                 is set; deleting an org destroys its communities too. Changes to the
                 switch land within about 60 s.
  guide          The WayAI Guide: ONE production chat hub, owned by WayAI, whose agent answers
                 questions about the platform. While \`set-hub\` has named it, every account
                 provisioned is enrolled in it as an end user and every signed-in person reaches
                 it from the help entry ("Ask WayAI"); its owning org pays. \`clear\` stops new
                 enrollment and hides the help entry; people already enrolled keep their row.
                 Changes land within about 60 s.

Types (do): ${VALID_TYPES.join(' | ')}
Tables (analytics): ${VALID_ANALYTICS_TABLES.join(' | ')}

Entity id forms (do):
  <uuid>          Domain entity id (hub_id, org_id, conversation_id, user_id, thread_id, community_id) —
                  hashed via idFromName, same path as every other route.
  hex:<64-hex>    Raw CF DO hex id — resolved via idFromString. Use when you
                  only have the CF-level id (e.g. from orphan-audit logs, CF
                  traces, zombie instances with no registry entry).
  platform        Singleton — literal string, hex: form not supported.

Examples:
  wayai admin do tables hub 018fab...
  wayai admin do read conversation <conv-id> message --limit 5 --json
  wayai admin analytics read conversation --conversation-id <conv-id>
  wayai admin archive <hub-id> <conv-id>
  wayai admin archive <hub-id> <conv-id> --message-id <msg-id> --json
  wayai admin observability <hub-id> <conv-id>
  wayai admin observability <hub-id> <conv-id> --message-id <msg-id> --json
  wayai admin hubs repair-branching-orphans --hub <prod-hub-id>
  wayai admin hubs repair-branching-orphans --hub <prod-hub-id> --id connection:<id> --dry-run
  wayai admin hubs repair-knowledge-blobs --all
  wayai admin hubs repair-knowledge-blobs --hub <hub-id> --apply
  wayai admin orgs repair-legacy-grants --all --dry-run
  wayai admin orgs repair-legacy-grants --org <org-id> --yes
  wayai admin harness disable
  wayai admin harness mass-destroy --all --dry-run
  wayai admin harness mass-destroy --hub <hub-id> --yes
  wayai admin harness enable

Audit + rate limit:
  Every successful read is logged as admin_debug_read (queryable in Grafana
  Loki — info-level events are NOT sent to Sentry). Capped at 120 reads/hour/user
  across all sources. Platform-admin grant required.
`.trim());
}
